import { database } from '@/firebase/config'
import type { ICareerResponse, IUser } from '@shared/types/base'
import { get, ref as dbRef, update } from 'firebase/database'

import { apiService } from '@/services/apiService'

export const getInstitutionName = async (institutionId: string): Promise<string> => {
	const snapshot = await get(dbRef(database, `institutions/${institutionId}/nombreInstitucion`))
	return snapshot.val() || 'Institución'
}
export const getUserData = async (userId: string): Promise<IUser> => {
	try {
		const uid = userId?.trim()
		if (!uid) return {} as IUser
		const snapshot = await get(dbRef(database, `users/${uid}`))
		return (snapshot.val() || {}) as IUser
	} catch (error) {
		// Realtime DB lanza "Permission denied" si las reglas no permiten leer `users/{uid}`.
		console.error('[getUserData]', error)
		return {} as IUser
	}
}
/** Devuelve los datos del nodo o null si no hay id, error de red o permiso denegado (p. ej. institución inexistente para las reglas actuales). */
export const getInstitutionData = async (institutionId: string): Promise<unknown> => {
	const id = institutionId?.trim()
	if (!id) return null
	try {
		const snapshot = await get(dbRef(database, `institutions/${id}`))
		return snapshot.val() ?? {}
	} catch (error) {
		console.error('[getInstitutionData]', error)
		return null
	}
}
/**
 * @description Verifica si una institución existe validando primero los permisos del usuario
 */
export const existsInstitution = async (institutionId: string): Promise<boolean> => {
	const snapshot = await get(dbRef(database, `institutions/${institutionId}`))
	return snapshot.exists()
}
export const existsByIdNombreInstitucion = async (idNombreInstitucion: number, institutionId: string): Promise<boolean> => {
	const snapshot = await get(dbRef(database, `institutions/${institutionId}/idNombreInstitucion/${idNombreInstitucion}`))
	return snapshot.val() !== idNombreInstitucion
}
export const getInstitutionCounter = async (): Promise<number> => {
	const snapshot = await get(dbRef(database, 'institutions/institutionsCounter'))
	return snapshot.val()
}
export const getIdNombreInstitucion = async (institutionId: string): Promise<number> => {
	const snapshot = await get(dbRef(database, `institutions/${institutionId}/idNombreInstitucion`))
	return snapshot.val() || 0
}
export const getUserInstitutions = async (userId: string): Promise<string[]> => {
	const snapshot = await get(dbRef(database, `users/${userId}/institutions`))
	return snapshot.val() || []
}
export const updateUserInstitutionAccess = async (userId: string, institutionId: string, hasAccess: boolean): Promise<void> => {
	try {
		await apiService.post('updateUserInstitutionAccess', { userId, institutionId, hasAccess })
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : 'Error al actualizar acceso a institución'
		throw new Error(message)
	}
}

export const getInstitutionCarreras = async (institutionId: string): Promise<{ [key: string]: ICareerResponse }> => {
	const snapshot = await get(dbRef(database, `institutions/${institutionId}/Carrera`))
	return snapshot.val() || {}
}
export const updateUserDataInstitution = async (userId: string, institutionId: string): Promise<void> => {
	try {
		await update(dbRef(database, `users/${userId}/userData`), { institution: institutionId })
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : 'Error al actualizar datos de usuario de institución'
		throw new Error(message)
	}
}
export const updateUserData = async (userId: string, userData: Partial<IUser>): Promise<void> => {
	try {
		await update(dbRef(database, `users/${userId}`), { ...userData })
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : 'Error al actualizar datos de usuario'
		throw new Error(message)
	}
}

export const normalizarFecha = (fecha: string) => {
	if (typeof fecha === 'string' && fecha.includes('/')) {
		const partes = fecha.split('/')
		if (partes.length === 3) {
			// Si el formato es d/m/yyyy o dd/mm/yyyy
			const dia = partes[0].padStart(2, '0')
			const mes = partes[1].padStart(2, '0')
			let anio = partes[2]

			// Manejar años de 2 dígitos (asumiendo años 2000+)
			if (anio.length === 2) {
				anio = '20' + anio
			}

			return `${anio}-${mes}-${dia}`
		}
	}
	return fecha
}

/**
 * @description Formatea un precio con formato de moneda
 */
export const formatPrice = (value: number | undefined): string => {
	if (value === undefined || value === null) return '$0.00'
	return new Intl.NumberFormat('es-MX', {
		style: 'currency',
		currency: 'MXN'
	}).format(value)
}
/**
 * @description Formatea un número con separadores de miles
 */
export const formatNumber = (value: number | undefined): string => {
	if (value === undefined || value === null) return '0'
	return new Intl.NumberFormat('es-MX').format(value)
}

export const isNearExpiry = (date: unknown): boolean => {
	if (!date) return false
	const expiryDate = new Date(date as string)
	const today = new Date()
	const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
	return daysUntilExpiry <= 90 && daysUntilExpiry > 0
}

export const daysUntilExpiry = (date: unknown): number => {
	if (!date) return 0
	const expiryDate = new Date(date as string)
	const today = new Date()
	return Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
}

export const isExpired = (date: unknown): boolean => {
	if (!date) return false
	const expiryDate = new Date(date as string)
	const today = new Date()
	return expiryDate < today
}
export const isCicloEscolarValid = (cicloEscolar: string): boolean => /^(\d{4})-\d{1}$/.test(cicloEscolar.trim())
const MAX_CORREO_LENGTH = 254

function _hasSpaceOrAt(value: string): boolean {
	for (let i = 0; i < value.length; i++) {
		const code = value.charCodeAt(i)
		if (code === 64 || code <= 32) return true
	}
	return false
}
/** Misma semántica que shared/modules/puiUtils.ts — TLD ≥ 2 letras, sin etiquetas vacías. */
export function _isEmailFormatValid(correo: string): boolean {
	if (correo.length === 0 || correo.length > MAX_CORREO_LENGTH) return false

	const at = correo.indexOf('@')
	if (at <= 0 || at !== correo.lastIndexOf('@')) return false

	const local = correo.slice(0, at)
	const domain = correo.slice(at + 1)
	if (local.length === 0 || domain.length === 0) return false
	if (_hasSpaceOrAt(local) || _hasSpaceOrAt(domain)) return false

	let labelStart = 0
	let labelCount = 0
	for (let i = 0; i <= domain.length; i++) {
		if (i !== domain.length && domain.charCodeAt(i) !== 46) continue

		const labelLen = i - labelStart
		if (labelLen === 0) return false

		labelCount++
		if (i === domain.length) {
			if (labelLen < 2) return false
			for (let j = labelStart; j < i; j++) {
				const c = domain.charCodeAt(j)
				const isLetter = (c >= 65 && c <= 90) || (c >= 97 && c <= 122)
				if (!isLetter) return false
			}
		}

		labelStart = i + 1
	}

	return labelCount >= 2
}

export const isEmailValid = (email: string): boolean => _isEmailFormatValid(email.trim())
